import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, uploadUrl } from "@/lib/api";
import { buildWhatsAppUrl } from "@/lib/whatsapp";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { PhotoUploadCrop } from "@/components/PhotoUploadCrop";
import ClubCombobox from "@/components/ClubCombobox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Loader2, Plus, ShieldCheck, UserPlus } from "lucide-react";

const ADD_NEW = "__ADD_NEW__";
const NONE = "__NONE__";
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0"));

function parseDayMonth(value) {
    if (!value) return { day: "", month: "" };
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (iso) return { day: iso[3], month: MONTHS_SHORT[parseInt(iso[2], 10) - 1] || "" };
    const m = /^(\d{1,2})-([A-Z][a-z]{2})$/.exec(value);
    if (m) return { day: m[1].padStart(2, "0"), month: m[2] };
    return { day: "", month: "" };
}

function DayMonthPicker({ value, onChange, testid }) {
    const parsed = parseDayMonth(value);
    const [day, setDay] = useState(parsed.day);
    const [month, setMonth] = useState(parsed.month);
    const lastRef = useRef(value || "");
    useEffect(() => {
        if ((value || "") !== lastRef.current) {
            const p = parseDayMonth(value);
            setDay(p.day); setMonth(p.month);
            lastRef.current = value || "";
        }
    }, [value]);
    const emit = (d, mo) => {
        const next = d && mo ? `${d}-${mo}` : "";
        lastRef.current = next;
        onChange(next);
    };
    return (
        <div className="grid grid-cols-2 gap-2">
            <Select value={day || undefined} onValueChange={(d) => { setDay(d); emit(d, month); }}>
                <SelectTrigger data-testid={`${testid}-day`}><SelectValue placeholder="Day" /></SelectTrigger>
                <SelectContent className="max-h-64">
                    {DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
            </Select>
            <Select value={month || undefined} onValueChange={(mo) => { setMonth(mo); emit(day, mo); }}>
                <SelectTrigger data-testid={`${testid}-month`}><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent>
                    {MONTHS_SHORT.map((mo) => <SelectItem key={mo} value={mo}>{mo}</SelectItem>)}
                </SelectContent>
            </Select>
        </div>
    );
}

function CommitteeCombo({ list, parentId, value, onChange, placeholder, disabled, testid, allowEmpty = false }) {
    const [addingNew, setAddingNew] = useState(false);
    const [customName, setCustomName] = useState("");
    const options = useMemo(
        () => (list || []).filter((c) => (c.parent_id || null) === (parentId || null)),
        [list, parentId],
    );
    useEffect(() => { setAddingNew(false); setCustomName(""); }, [parentId]);
    const handleSelect = (v) => {
        if (v === ADD_NEW) { setAddingNew(true); setCustomName(""); return; }
        if (v === NONE) { onChange({ name: "", id: null }); setAddingNew(false); return; }
        const found = (list || []).find((c) => c.id === v);
        if (found) onChange({ name: found.name, id: found.id });
    };
    const commitCustom = () => {
        const name = customName.trim();
        if (!name) return;
        onChange({ name, id: null });
        setAddingNew(false);
    };
    const selectValue = value?.id ? value.id : (value?.name ? value.name : "");
    if (addingNew) {
        return (
            <div className="flex gap-2">
                <Input autoFocus value={customName} onChange={(e) => setCustomName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitCustom(); } if (e.key === "Escape") { setAddingNew(false); setCustomName(""); } }}
                    placeholder="Type new name and press Enter" data-testid={`${testid}-custom-input`} />
                <Button type="button" size="sm" onClick={commitCustom} className="rounded-full" data-testid={`${testid}-custom-save`}>Add</Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => { setAddingNew(false); setCustomName(""); }} data-testid={`${testid}-custom-cancel`}>Cancel</Button>
            </div>
        );
    }
    return (
        <Select value={selectValue} onValueChange={handleSelect} disabled={disabled}>
            <SelectTrigger data-testid={testid}>
                <SelectValue placeholder={placeholder || "Select…"}>
                    {value?.name || <span className="text-muted-foreground">{placeholder}</span>}
                </SelectValue>
            </SelectTrigger>
            <SelectContent>
                {allowEmpty && <SelectItem value={NONE}><span className="text-muted-foreground italic">(none)</span></SelectItem>}
                {options.length === 0 && !allowEmpty && <div className="px-2 py-1.5 text-xs text-muted-foreground">No options — add one below.</div>}
                {options.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                <SelectItem value={ADD_NEW}><span className="flex items-center gap-1.5 text-rotary-blue"><Plus className="h-3.5 w-3.5" /> Add new…</span></SelectItem>
            </SelectContent>
        </Select>
    );
}

function MemberCard({ m, selected, onSelect }) {
    const initials = (m.name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
    // Iter 100i · Registry-only rows have no person_id yet.
    const key = m.person_id || m.phone;
    const selKey = selected ? (selected.person_id || selected.phone) : "";
    const isSelected = selKey && selKey === key;
    return (
        <div
            className={`flex items-center gap-3 rounded-xl border p-3 transition-colors ${isSelected ? "border-rotary-blue bg-rotary-blue/5" : "bg-muted/20 hover:bg-muted/30 cursor-pointer"}`}
            onClick={onSelect}
            data-testid={`assign-role-match-card-${key}`}
        >
            {m.photo_filename ? (
                <img src={uploadUrl(m.photo_filename, m.updated_at)} alt="" className="h-14 w-14 rounded-full object-cover border" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            ) : (
                <div className="h-14 w-14 grid place-items-center rounded-full bg-rotary-blue/10 text-rotary-blue font-medium">{initials}</div>
            )}
            <div className="min-w-0 flex-1">
                <p className="rotary-heading text-sm truncate">{m.name}</p>
                <p className="text-xs text-muted-foreground truncate">{m.phone} · {m.club_name || "—"}</p>
                {m.from_registry && (
                    <p className="text-[11px] text-rotary-blue truncate mt-0.5">From Member Registry — will be added to this district on save</p>
                )}
                {(m.current_roles || []).length > 0 && (
                    <p className="text-[11px] text-muted-foreground truncate mt-0.5">{m.current_roles.length} existing role{m.current_roles.length !== 1 ? "s" : ""}</p>
                )}
            </div>
        </div>
    );
}

/**
 * AssignRoleDialog — single unified screen for assigning a role to a member.
 * Replaces the older "Add member" and "Create role" dialogs.
 *
 * Flow:
 *  1. Admin types a phone number. When it reaches 10 digits, lookup runs
 *     automatically (no submit button).
 *  2a. Match found → member card appears + role dropdowns unlock.
 *  2b. No match → "not in registry" message + full profile form appears
 *      with Name (required) and Phone (locked) pre-filled. Admin fills the
 *      rest, saves → the just-created member is auto-selected and role
 *      dropdowns unlock in place.
 *  3. Admin picks Committee (required) / Sub / 2nd Sub / Role (required)
 *     and clicks "Assign role" — appends one row to the year's tab in the
 *     master Google Sheet. Multi-role: never overwrites existing roles.
 *
 *  WhatsApp is NOT sent automatically anywhere in this flow.
 */
export function AssignRoleDialog({ open, onOpenChange, yid, onSaved }) {
    // Unified search: accepts a phone (any formatting — spaces, dashes,
    // +91 prefix; last 10 digits are used) OR a name (case-insensitive
    // substring, debounced after 3 chars). Iter 100g.
    const [query, setQuery] = useState("");
    const [phone, setPhone] = useState("");   // canonical 10-digit phone
    // -- source of truth for the create flow (locked field / API call)
    const [looking, setLooking] = useState(false);
    const [matches, setMatches] = useState([]);
    const [selected, setSelected] = useState(null);
    const [noMatch, setNoMatch] = useState(false);
    // Tracks the last kind of search that produced the current results.
    // "phone" | "name" | ""  — used to decide whether "no match" should
    // offer the Create-member form (only when we have a 10-digit phone).
    const [searchKind, setSearchKind] = useState("");

    // Committee tree + role assignment
    const [committees, setCommittees] = useState([]);
    const [lvl1, setLvl1] = useState({ name: "", id: null });
    const [sub, setSub] = useState({ name: "", id: null });
    const [lvl2sub, setLvl2sub] = useState({ name: "", id: null });
    const [position, setPosition] = useState("");
    const [saving, setSaving] = useState(false);

    // New member profile form (only shown when phone not found)
    const [profile, setProfile] = useState({
        name: "", email: "", club_name: "", classification: "",
        birthday: "", postal_address: "",
        spouse_name: "", spouse_birthday: "", anniversary: "",
    });
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState("");
    const [spousePhotoFile, setSpousePhotoFile] = useState(null);
    const [spousePhotoPreview, setSpousePhotoPreview] = useState("");
    const [savingNew, setSavingNew] = useState(false);
    // After saveAll succeeds in the create flow, we hold onto the just-created
    // member so the admin can trigger the WhatsApp profile-completion message
    // manually before closing the dialog.
    const [justCreated, setJustCreated] = useState(null);

    const reset = () => {
        setQuery(""); setPhone(""); setLooking(false); setMatches([]); setSelected(null); setNoMatch(false); setSearchKind("");
        setLvl1({ name: "", id: null }); setSub({ name: "", id: null });
        setLvl2sub({ name: "", id: null }); setPosition("");
        setProfile({ name: "", email: "", club_name: "", classification: "", birthday: "",
            postal_address: "", spouse_name: "", spouse_birthday: "", anniversary: "" });
        setPhotoFile(null); setPhotoPreview("");
        setSpousePhotoFile(null); setSpousePhotoPreview("");
        setJustCreated(null);
    };

    const onClose = (v) => { onOpenChange(v); if (!v) setTimeout(reset, 300); };

    // Load committee tree once dialog opens
    useEffect(() => {
        if (!open || !yid) return;
        (async () => {
            try { const { data } = await api.get(`/scoped/district-years/${yid}/committees`); setCommittees(data || []); }
            catch { setCommittees([]); }
        })();
    }, [open, yid]);

    // Cascade resets when a higher level changes
    useEffect(() => { setSub({ name: "", id: null }); setLvl2sub({ name: "", id: null }); }, [lvl1.id, lvl1.name]);
    useEffect(() => { setLvl2sub({ name: "", id: null }); }, [sub.id, sub.name]);

    // Unified search effect. Debounced ~300ms. Rules:
    //  - Digits/spaces/dashes/+()  →  phone mode. Extract last 10 digits;
    //    only run lookup when it reaches exactly 10 (backend enforces).
    //  - Anything containing a letter  →  name mode. Fires when the
    //    trimmed query length ≥ 3.
    // On success: populates matches + auto-selects a single result.
    // On 404 from a 10-digit phone search: shows the "not in registry"
    // create form pre-filled with the phone. On 404 from a name search:
    // shows a soft "no match" hint but does NOT open the create form —
    // the admin still needs to supply a phone to create.
    useEffect(() => {
        if (!open || !yid) return;
        const raw = String(query || "").trim();
        const hasLetter = /[a-zA-Z]/.test(raw);
        const digits = raw.replace(/\D+/g, "").slice(-10);

        // Reset lookup state whenever the search is below thresholds
        if (!raw) {
            setMatches([]); setSelected(null); setNoMatch(false);
            setSearchKind(""); setPhone("");
            return;
        }
        if (hasLetter) {
            setPhone("");    // name mode invalidates any prior canonical phone
            if (raw.length < 3) {
                setMatches([]); setSelected(null); setNoMatch(false); setSearchKind("");
                return;
            }
        } else {
            if (digits.length !== 10) {
                setMatches([]); setSelected(null); setNoMatch(false); setSearchKind("");
                setPhone(digits);  // keep partial so the create form can lock once complete
                return;
            }
            setPhone(digits);
        }

        let cancelled = false;
        const timer = setTimeout(async () => {
            setLooking(true); setMatches([]); setSelected(null); setNoMatch(false);
            const kind = hasLetter ? "name" : "phone";
            setSearchKind(kind);
            try {
                const params = kind === "phone" ? { phone: digits } : { name: raw };
                const { data } = await api.get(`/scoped/district-years/${yid}/members/lookup`, { params });
                if (cancelled) return;
                const results = data.matches || [];
                setMatches(results);
                if (results.length === 1) setSelected(results[0]);
            } catch (e) {
                if (cancelled) return;
                if (e?.response?.status === 404) {
                    setNoMatch(true);
                    if (kind === "phone") setProfile((p) => ({ ...p, name: "" }));
                } else {
                    toast.error(e?.response?.data?.detail || "Lookup failed");
                }
            } finally {
                if (!cancelled) setLooking(false);
            }
        }, 300);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [query, open, yid]);

    // Called by PhotoUploadCrop with the already-cropped 272×272 JPEG File.
    const acceptCroppedPhoto = (file, isSpouse) => {
        const preview = URL.createObjectURL(file);
        if (isSpouse) { setSpousePhotoFile(file); setSpousePhotoPreview(preview); }
        else { setPhotoFile(file); setPhotoPreview(preview); }
    };

    // Create new member (bare person + full profile in two calls). Then
    // auto-select so the role dropdowns unlock in place.
    const saveNewMemberOnly = async () => {
        const trimmedName = profile.name.trim();
        const digits = String(phone || "").replace(/\D+/g, "").slice(-10);
        if (!trimmedName) { toast.error("Name is required"); return null; }
        if (digits.length !== 10) { toast.error("Phone must be exactly 10 digits"); return null; }
        // Step 1: create bare person + year_role. Backend fires a
        // sheet-append in the background — that's fine, subsequent edit
        // will run a targeted update once the profile is filled in.
        const { data: created } = await api.post(`/scoped/district-years/${yid}/members`, {
            phone: digits, name: trimmedName, email: profile.email.trim(), committee_assignments: [],
        });
        const yrid = created?.year_role?.id;
        if (!yrid) throw new Error("Missing year_role id");

        // Step 2: send the full profile (photos included) via the same
        // endpoint the profile-edit uses. Sends only non-empty fields
        // so we don't overwrite anything with blanks.
        const fd = new FormData();
        fd.append("name", trimmedName);
        fd.append("phone", digits);
        if (profile.email.trim()) fd.append("email", profile.email.trim());
        if (profile.birthday) fd.append("birthday", profile.birthday);
        if (profile.postal_address.trim()) fd.append("postal_address", profile.postal_address.trim());
        // Iter 82b: club_name + classification from the Add Member dialog
        if (profile.club_name.trim()) fd.append("club_name", profile.club_name.trim());
        if (profile.classification.trim()) fd.append("classification", profile.classification.trim().slice(0, 70));
        if (profile.spouse_name.trim()) fd.append("spouse_name", profile.spouse_name.trim());
        if (profile.spouse_birthday) fd.append("spouse_birthday", profile.spouse_birthday);
        if (profile.anniversary) fd.append("anniversary", profile.anniversary);
        if (photoFile) fd.append("photo", photoFile);
        if (spousePhotoFile) fd.append("spouse_photo", spousePhotoFile);

        const { data: updated } = await api.post(`/scoped/year-roles/${yrid}/edit`, fd);

        return {
            person_id: updated.id || created.id,
            year_role_id: updated.year_role?.id || yrid,
            form_token: updated.year_role?.form_token || created.year_role?.form_token || "",
            progress_pct: updated.year_role?.progress_pct || 0,
            name: updated.name || trimmedName,
            phone: updated.phone || digits,
            club_name: updated.club_name || "",
            classification: updated.classification || "",
            email: updated.email || profile.email.trim(),
            photo_filename: updated.photo_filename || "",
            current_roles: [],
        };
    };

    // Unified save button on the role section. Committee + position are
    // OPTIONAL for both flows:
    //  - Existing member: if lvl1/position are empty, save just confirms
    //    the record and closes. If both filled, a role is appended.
    //  - New member: only name is required. If lvl1/position are also
    //    filled, a role is appended on top of the create. Otherwise the
    //    profile is created and the admin can complete role assignment
    //    later.
    // "1st Level Committee is required" only kicks in when a position is
    // typed without a committee (or vice versa), to prevent orphaned rows.
    const saveAll = async () => {
        const trimmedPos = position.trim();
        const trimmedL1 = lvl1.name.trim();
        // Only require both when the admin actually started assigning a role
        // (i.e. filled one but not the other).
        if (trimmedL1 && !trimmedPos) { toast.error("Role (Position) is required to complete the role assignment, or clear the committee"); return; }
        if (trimmedPos && !trimmedL1) { toast.error("1st Level Committee is required to complete the role assignment, or clear the position"); return; }
        const wantRoleAssign = !!(trimmedL1 && trimmedPos);

        setSaving(true);
        const createFlow = !selected && noMatch;
        try {
            let target = selected;
            if (!target && noMatch) {
                // Create-first path
                setSavingNew(true);
                try {
                    target = await saveNewMemberOnly();
                } finally {
                    setSavingNew(false);
                }
                if (!target) return; // validation error inside saveNewMemberOnly
                setMatches([target]); setSelected(target); setNoMatch(false);
            }
            if (!target) { toast.error("Pick a member first"); return; }

            if (wantRoleAssign) {
                const { data } = await api.post(`/scoped/district-years/${yid}/roles`, {
                    phone: target.phone,
                    committee_1st_lvl: trimmedL1,
                    subcommittee: sub.name.trim(),
                    subcommittee_2nd_lvl: lvl2sub.name.trim(),
                    position: trimmedPos,
                    // Iter 100i · forward Registry-derived profile fields so
                    // the /roles endpoint can upsert the person on miss.
                    name: target.name || "",
                    email: target.email || "",
                    club_name: target.club_name || "",
                    classification: target.classification || "",
                });
                const sheetOk = data?.sheet?.ok;
                toast.success("Role assigned", {
                    description: sheetOk
                        ? "Added to Google Sheet (appended)"
                        : `Saved to database; sheet: ${data?.sheet?.reason || "skipped"}`,
                });
            } else if (createFlow) {
                // New member created without a role — that's fine, profile
                // completion can happen later. Sheet row for the member is
                // already queued by saveNewMemberOnly's profile edit call.
                toast.success("Member saved", {
                    description: "Profile can be completed later — no role assigned yet.",
                });
            } else if (target && (target.enrolled_in_year === false || target.from_registry)) {
                // Existing person in the master directory OR a Registry-only
                // row that hasn't been synced into Mongo yet. Save-without-role
                // should still add them to the year so they show up on the
                // dashboard. POST /members creates the year_role idempotently
                // and upserts the person (iter 100f + iter 100i).
                await api.post(`/scoped/district-years/${yid}/members`, {
                    phone: target.phone,
                    name: target.name || "",
                    email: target.email || "",
                    committee_assignments: [],
                });
                toast.success("Member added to this year", {
                    description: "Profile is on the dashboard — role assignment can happen later.",
                });
            } else {
                // Existing member already enrolled, no role changes — just a confirmation.
                toast.success("Confirmed", { description: "Nothing to change." });
            }

            if (createFlow && target?.form_token) {
                // Keep the dialog open so the admin can send the WhatsApp
                // profile-completion link manually. onSaved refreshes the
                // list in the background; the dialog only closes on Done.
                setJustCreated(target);
                onSaved && onSaved({ keepOpen: true });
            } else {
                onSaved && onSaved();
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save");
        } finally {
            setSaving(false);
        }
    };

    const digits = String(phone || "").replace(/\D+/g, "").slice(-10);
    // Only offer the "Create new member" form when the lookup was a
    // phone search that returned 404 and we have a full 10-digit
    // number to lock in. Name-only no-match doesn't open create.
    const showRoleForm = !!selected || (noMatch && searchKind === "phone" && digits.length === 10);
    const isCreateFlow = !selected && noMatch && searchKind === "phone" && digits.length === 10;

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="assign-role-dialog">
                <DialogHeader>
                    <DialogTitle className="rotary-heading text-2xl flex items-center gap-2">
                        <ShieldCheck className="h-5 w-5 text-rotary-blue" /> Add member
                    </DialogTitle>
                </DialogHeader>

                {/* Phone/name lookup (hidden after new-member creation success) */}
                {!justCreated && (
                <div>
                    <Label htmlFor="ar-search">Search by name or phone <span className="text-red-500">*</span></Label>
                    <div className="relative">
                        <Input id="ar-search" type="text" value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => { e.stopPropagation(); }}
                            inputMode="text"
                            placeholder="Type a name (≥ 3 letters) or a 10-digit phone"
                            data-testid="assign-role-search-input" />
                        {looking && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" data-testid="assign-role-loading-indicator" />
                        )}
                    </div>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                        Names search after 3 letters · phone matches the last 10 digits — spaces, dashes and +91 are ignored.
                    </p>
                    {noMatch && searchKind === "name" && (
                        <p className="mt-2 text-[11px] text-rotary-cranberry" data-testid="assign-role-name-no-match">
                            No member matched that name. Enter a phone number to add a new member.
                        </p>
                    )}
                </div>
                )}

                {/* Post-create success — WhatsApp profile-completion prompt */}
                {justCreated && (
                    <div className="mt-2 rounded-xl border border-emerald-300 bg-emerald-50 p-5 space-y-4" data-testid="assign-role-created-panel">
                        <div className="flex items-start gap-3">
                            <CheckCircle2 className="h-6 w-6 text-emerald-600 mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <p className="rotary-heading text-lg">Added — {justCreated.name}</p>
                                <p className="text-sm text-muted-foreground truncate">{justCreated.phone} · profile saved</p>
                            </div>
                        </div>
                        <div className="rounded-lg border bg-white p-3 space-y-2">
                            <p className="label-eyebrow text-[10px]">Ask the member to complete their profile</p>
                            <p className="text-xs text-muted-foreground">
                                No message was sent automatically. Tap the button below to open WhatsApp with a pre-composed profile-completion message ready to send.
                            </p>
                            <div className="flex flex-wrap gap-2 pt-1">
                                <a
                                    href={buildWhatsAppUrl(justCreated.name, justCreated.phone, justCreated.form_token, justCreated.progress_pct || 0)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-medium text-white hover:bg-[#128C7E] transition-colors"
                                    data-testid="assign-role-whatsapp-btn"
                                    title="Open WhatsApp with the profile link"
                                >
                                    <WhatsAppIcon className="h-4 w-4" /> Send profile link on WhatsApp
                                </a>
                                <Button variant="outline"
                                    onClick={async () => {
                                        const link = `${window.location.origin}/form/${justCreated.form_token}`;
                                        try { await navigator.clipboard.writeText(link); toast.success("Profile link copied"); }
                                        catch { toast.error("Copy failed — long-press the WhatsApp button instead"); }
                                    }}
                                    className="rounded-full"
                                    data-testid="assign-role-copy-link-btn"
                                >
                                    Copy link
                                </Button>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2 pt-1">
                            <Button variant="outline"
                                onClick={() => { setJustCreated(null); reset(); }}
                                className="rounded-full"
                                data-testid="assign-role-add-another-btn">
                                Add another
                            </Button>
                            <Button
                                onClick={() => { onOpenChange(false); }}
                                className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky"
                                data-testid="assign-role-done-btn">
                                Done
                            </Button>
                        </div>
                    </div>
                )}

                {/* Match card(s) */}
                {matches.length > 0 && !justCreated && (
                    <div className="mt-2 space-y-2" data-testid="assign-role-matches">
                        <p className="label-eyebrow text-[10px]">
                            {matches.length === 1 ? "Member found — confirm and assign" : `${matches.length} matches — click to select`}
                        </p>
                        <div className="max-h-40 overflow-y-auto pr-1 space-y-2">
                            {matches.map((m) => (
                                <MemberCard key={m.person_id} m={m}
                                    selected={selected?.person_id === m.person_id}
                                    onSelect={() => setSelected(m)} />
                            ))}
                        </div>
                        {selected && (selected.current_roles || []).length > 0 && (
                            <div className="rounded-lg border bg-white p-2.5">
                                <p className="label-eyebrow text-[10px] mb-1.5">Existing roles (kept — new role is added, never overwritten)</p>
                                <ul className="space-y-1 text-xs">
                                    {selected.current_roles.map((r, i) => {
                                        const path = [r.committee_1st_lvl, r.subcommittee, r.subcommittee_2nd_lvl].filter(Boolean).join(" › ");
                                        return (
                                            <li key={`${r.committee_1st_lvl}|${r.subcommittee}|${r.subcommittee_2nd_lvl}|${r.position}|${i}`} className="truncate">
                                                <span className="font-medium">{r.position || "—"}</span>{" · "}
                                                <span className="text-muted-foreground">{path}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {/* New-member profile form (shown only when phone lookup returned no match) */}
                {noMatch && searchKind === "phone" && digits.length === 10 && !selected && !justCreated && (
                    <div className="mt-2 rounded-xl border border-dashed border-rotary-blue/40 bg-rotary-blue/[0.03] p-4 space-y-4">
                        <div className="flex items-center gap-2">
                            <UserPlus className="h-4 w-4 text-rotary-blue" />
                            <p className="rotary-heading text-sm">This number is not in the registry - Add member to the registry</p>
                        </div>

                        <div className="space-y-1.5">
                            <span className="label-eyebrow text-[10px]">Member photo</span>
                            <PhotoUploadCrop
                                subject="member"
                                previewSrc={photoPreview}
                                onCropped={(file) => acceptCroppedPhoto(file, false)}
                                onRemove={photoPreview ? (() => { setPhotoFile(null); setPhotoPreview(""); }) : undefined}
                                testid="assign-role-new-photo"
                            />
                        </div>

                        <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                                <Label>Name <span className="text-red-500">*</span></Label>
                                <Input value={profile.name}
                                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                    placeholder="Full name"
                                    data-testid="assign-role-new-name-input" />
                            </div>
                            <div>
                                <Label>Phone <span className="text-red-500">*</span></Label>
                                <Input value={digits} readOnly disabled
                                    data-testid="assign-role-new-phone-locked" />
                                <p className="mt-1 text-[11px] text-muted-foreground">Locked — admin-only. Members cannot change this.</p>
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input type="email" value={profile.email}
                                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                                    data-testid="assign-role-new-email-input" />
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Club name</Label>
                                <ClubCombobox
                                    value={profile.club_name}
                                    onChange={(name) => setProfile({ ...profile, club_name: name })}
                                    placeholder="e.g. Rotary Club of Bangalore Midtown"
                                    testId="assign-role-new-club-input"
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Classification</Label>
                                <Input value={profile.classification}
                                    onChange={(e) => setProfile({ ...profile, classification: e.target.value.slice(0, 70) })}
                                    maxLength={70}
                                    placeholder="e.g. Chartered Accountant"
                                    data-testid="assign-role-new-classification-input" />
                                <p className="mt-1 text-[10px] text-right text-muted-foreground tabular-nums">
                                    {(profile.classification || "").length}/70
                                </p>
                            </div>
                            <div>
                                <Label>Birthday</Label>
                                <DayMonthPicker value={profile.birthday}
                                    onChange={(v) => setProfile({ ...profile, birthday: v })}
                                    testid="assign-role-new-birthday" />
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Communication address</Label>
                                <Textarea rows={2} value={profile.postal_address}
                                    onChange={(e) => setProfile({ ...profile, postal_address: e.target.value })}
                                    data-testid="assign-role-new-address-input" />
                            </div>
                        </div>

                        <div className="rounded-xl border bg-white/50 p-3 space-y-3">
                            <span className="label-eyebrow text-[10px]">Spouse (optional)</span>
                            <div className="grid gap-3 sm:grid-cols-2">
                                <div>
                                    <Label>Spouse name</Label>
                                    <Input value={profile.spouse_name}
                                        onChange={(e) => setProfile({ ...profile, spouse_name: e.target.value.replace(/[^A-Za-z ]/g, "") })}
                                        placeholder="Letters only (A-Z)"
                                        data-testid="assign-role-new-spouse-name-input" />
                                </div>
                                <div className="space-y-1.5">
                                    <span className="label-eyebrow text-[10px]">Spouse photo</span>
                                    <PhotoUploadCrop
                                        subject="spouse"
                                        previewSrc={spousePhotoPreview}
                                        onCropped={(file) => acceptCroppedPhoto(file, true)}
                                        onRemove={spousePhotoPreview ? (() => { setSpousePhotoFile(null); setSpousePhotoPreview(""); }) : undefined}
                                        testid="assign-role-new-spouse-photo"
                                    />
                                </div>
                                <div>
                                    <Label>Spouse birthday</Label>
                                    <DayMonthPicker value={profile.spouse_birthday}
                                        onChange={(v) => setProfile({ ...profile, spouse_birthday: v })}
                                        testid="assign-role-new-spouse-birthday" />
                                </div>
                                <div>
                                    <Label>Anniversary</Label>
                                    <DayMonthPicker value={profile.anniversary}
                                        onChange={(v) => setProfile({ ...profile, anniversary: v })}
                                        testid="assign-role-new-anniversary" />
                                </div>
                            </div>
                        </div>

                        <div className="pt-1">
                            <p className="text-[11px] text-muted-foreground leading-snug">
                                Name and phone are admin-only. Members can edit their own email, address, photo, and other personal details from their profile link.
                                No WhatsApp is sent automatically — you can send the profile link manually from the members list.
                            </p>
                        </div>
                    </div>
                )}

                {/* Role assignment dropdowns — visible when a member is selected OR
                    when creating a new member (noMatch case). Same-screen flow: admin
                    fills profile + role fields, one Save button does both actions. */}
                {showRoleForm && !justCreated && (
                    <div className="mt-3 rounded-xl border bg-white p-4 space-y-3" data-testid="assign-role-role-section">
                        <p className="label-eyebrow text-[10px]">
                            {isCreateFlow
                                ? "Assign a role (optional) — you can complete this later. Leave blank to just save the new member."
                                : "Assign a role (optional) — fill both fields to append a Sheet row, or click Save with them blank to just confirm."}
                        </p>
                        <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                                <Label>1st Level Committee</Label>
                                <CommitteeCombo list={committees} parentId={null} value={lvl1} onChange={setLvl1}
                                    placeholder="Select or add committee" testid="assign-role-l1-combo" />
                            </div>
                            <div>
                                <Label>Sub-committee</Label>
                                <CommitteeCombo list={committees} parentId={lvl1.id} value={sub} onChange={setSub}
                                    placeholder={lvl1.name ? "Select or add sub-committee" : "Pick a 1st level first"}
                                    disabled={!lvl1.name} testid="assign-role-sub-combo" allowEmpty />
                            </div>
                            <div>
                                <Label>2nd Level Sub-committee</Label>
                                <CommitteeCombo list={committees} parentId={sub.id} value={lvl2sub} onChange={setLvl2sub}
                                    placeholder={sub.name ? "Select or add 2nd level" : "Pick a sub-committee first"}
                                    disabled={!sub.name} testid="assign-role-l2-combo" allowEmpty />
                            </div>
                            <div>
                                <Label>Role (Position)</Label>
                                <Input value={position} onChange={(e) => setPosition(e.target.value)}
                                    placeholder="e.g. Chair, Member, Trainer"
                                    data-testid="assign-role-position-input" />
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2 pt-1">
                            <Button onClick={saveAll}
                                disabled={saving || savingNew || (isCreateFlow && !profile.name.trim())}
                                className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky"
                                data-testid="assign-role-save-btn">
                                {(saving || savingNew) ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
                                Save
                            </Button>
                            {isCreateFlow && !profile.name.trim() && (
                                <p className="text-[11px] text-rotary-cranberry self-center">Enter a name above to enable save.</p>
                            )}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default AssignRoleDialog;
